export interface PluginDef {
  id: string;
  name: string;
  mode: 'passive' | 'active';
  category: 'domain' | 'cidr' | 'api-key';
  needsApiKey: boolean;
  description: string;
}

// Authoritative plugin catalog — mirrors pluginNames in pius/pkg/lib/discovery.go
export const ALL_PLUGINS: PluginDef[] = [
  // Domain plugins (passive)
  {
    id: 'crt-sh',
    name: 'crt.sh',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Certificate Transparency log search for discovering subdomains.',
  },
  {
    id: 'apollo',
    name: 'Apollo',
    mode: 'passive',
    category: 'domain',
    needsApiKey: true,
    description:
      'Sales intelligence platform for contact and company discovery.',
  },
  {
    id: 'github-org',
    name: 'GitHub Org',
    mode: 'passive',
    category: 'domain',
    needsApiKey: true,
    description:
      'Discovers repositories and assets associated with a GitHub organization.',
  },
  {
    id: 'gleif',
    name: 'GLEIF',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Global Legal Entity Identifier Foundation registry for corporate entity lookup.',
  },
  {
    id: 'passive-dns',
    name: 'Passive DNS',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Historical DNS record lookups and passive domain intelligence.',
  },
  {
    id: 'reverse-whois',
    name: 'Reverse WHOIS',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Finds domains registered by the same organization via WHOIS data.',
  },
  {
    id: 'whois',
    name: 'WHOIS',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description: 'Domain registration and ownership information lookup.',
  },
  {
    id: 'urlscan',
    name: 'URLScan',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Web page scanning service for discovering related URLs and assets.',
  },
  {
    id: 'wayback',
    name: 'Wayback Machine',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Internet Archive crawls for discovering historical URLs and subdomains.',
  },
  {
    id: 'wikidata',
    name: 'Wikidata',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Structured knowledge base for entity and organization discovery.',
  },
  {
    id: 'google-dorks',
    name: 'Google Dorks',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Google search operators for discovering exposed assets and subdomains.',
  },
  {
    id: 'reverse-ip',
    name: 'Reverse IP',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Finds domains hosted on the same IP address via reverse DNS lookup.',
  },
  {
    id: 'otx-alienvault',
    name: 'OTX AlienVault',
    mode: 'passive',
    category: 'domain',
    needsApiKey: false,
    description:
      'Open Threat Exchange threat intelligence for domain and IP indicators.',
  },
  // Domain plugins (active)
  {
    id: 'dns-brute',
    name: 'DNS Brute Force',
    mode: 'active',
    category: 'domain',
    needsApiKey: false,
    description:
      'Active subdomain enumeration via wordlist-based DNS brute forcing.',
  },
  {
    id: 'dns-zone-transfer',
    name: 'DNS Zone Transfer',
    mode: 'active',
    category: 'domain',
    needsApiKey: false,
    description:
      'Attempts DNS zone transfers to enumerate all records for a domain.',
  },
  {
    id: 'doh-enum',
    name: 'DoH Enumeration',
    mode: 'active',
    category: 'domain',
    needsApiKey: false,
    description:
      'DNS-over-HTTPS enumeration for discovering subdomains via DoH resolvers.',
  },
  {
    id: 'dns-permutation',
    name: 'DNS Permutation',
    mode: 'active',
    category: 'domain',
    needsApiKey: false,
    description:
      'Generates and resolves permutations of known subdomains to find new assets.',
  },
  {
    id: 'favicon-hash',
    name: 'Favicon Hash',
    mode: 'active',
    category: 'domain',
    needsApiKey: false,
    description:
      'Identifies related hosts by matching favicon hashes across the internet.',
  },
  // CIDR plugins (passive)
  {
    id: 'asn-bgp',
    name: 'ASN BGP',
    mode: 'passive',
    category: 'cidr',
    needsApiKey: false,
    description:
      'Discovers IP ranges assigned to an organization via BGP routing data.',
  },
  {
    id: 'reverse-rir',
    name: 'Reverse RIR',
    mode: 'passive',
    category: 'cidr',
    needsApiKey: false,
    description:
      'Regional Internet Registry lookup to find IP allocations by organization.',
  },
  {
    id: 'edgar',
    name: 'EDGAR',
    mode: 'passive',
    category: 'cidr',
    needsApiKey: false,
    description:
      'SEC EDGAR filings for discovering subsidiary and IP range disclosures.',
  },
  {
    id: 'arin',
    name: 'ARIN',
    mode: 'passive',
    category: 'cidr',
    needsApiKey: false,
    description:
      'American Registry for Internet Numbers lookup for North American IP ranges.',
  },
  {
    id: 'ripe',
    name: 'RIPE',
    mode: 'passive',
    category: 'cidr',
    needsApiKey: false,
    description:
      'RIPE NCC registry lookup for European and Middle Eastern IP allocations.',
  },
  {
    id: 'lacnic',
    name: 'LACNIC',
    mode: 'passive',
    category: 'cidr',
    needsApiKey: false,
    description:
      'Latin America and Caribbean IP registry lookup for regional IP ranges.',
  },
  {
    id: 'apnic',
    name: 'APNIC',
    mode: 'passive',
    category: 'cidr',
    needsApiKey: false,
    description:
      'Asia-Pacific IP registry lookup for regional network allocations.',
  },
  {
    id: 'afrinic',
    name: 'AfriNIC',
    mode: 'passive',
    category: 'cidr',
    needsApiKey: false,
    description:
      'African Network Information Centre registry for African IP allocations.',
  },
  // API-key plugins (passive — except censys which is active)
  {
    id: 'shodan',
    name: 'Shodan',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'Internet-wide port scanning and service enumeration intelligence.',
  },
  {
    id: 'dnsdb',
    name: 'DNSDB',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'Farsight Security passive DNS database for historical DNS lookups.',
  },
  {
    id: 'crunchbase',
    name: 'Crunchbase',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'Business intelligence for discovering subsidiaries and company relationships.',
  },
  {
    id: 'opencorporates',
    name: 'OpenCorporates',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'Global corporate registry for subsidiary and entity discovery.',
  },
  {
    id: 'proxycurl',
    name: 'Proxycurl',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'LinkedIn data enrichment for people and company intelligence.',
  },
  {
    id: 'diffbot',
    name: 'Diffbot',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'AI-powered web data extraction for entity and relationship discovery.',
  },
  {
    id: 'securitytrails',
    name: 'SecurityTrails',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'DNS and domain intelligence with historical data and subdomain enumeration.',
  },
  {
    id: 'virustotal',
    name: 'VirusTotal',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'Multi-engine malware analysis and threat intelligence for URLs and IPs.',
  },
  {
    id: 'binaryedge',
    name: 'BinaryEdge',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'Internet-wide scanning data for exposed services and vulnerabilities.',
  },
  {
    id: 'censys-org',
    name: 'Censys',
    mode: 'active',
    category: 'api-key',
    needsApiKey: true,
    description:
      'Internet-wide scanning with certificate, service, and host intelligence.',
  },
  {
    id: 'viewdns',
    name: 'ViewDNS',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'DNS lookup and reverse IP tools for domain and hosting intelligence.',
  },
  {
    id: 'whoxy',
    name: 'Whoxy',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'WHOIS reverse lookup service for discovering related domain portfolios.',
  },
  {
    id: 'builtwith',
    name: 'BuiltWith',
    mode: 'passive',
    category: 'api-key',
    needsApiKey: true,
    description:
      'Web tag lookup service for discovering domains sharing analytics tracking codes.',
  },
];

// Map pius_* integration IDs to short plugin names for hasMasterKey lookup
export const PIUS_PREFIX_TO_SHORT: Record<string, string> = {
  pius_shodan: 'shodan',
  pius_dnsdb: 'dnsdb',
  pius_crunchbase: 'crunchbase',
  pius_opencorporates: 'opencorporates',
  pius_proxycurl: 'proxycurl',
  pius_diffbot: 'diffbot',
  pius_github: 'github-org',
  pius_securitytrails: 'securitytrails',
  pius_virustotal: 'virustotal',
  pius_binaryedge: 'binaryedge',
  pius_apollo: 'apollo',
  pius_censys: 'censys-org',
  pius_viewdns: 'viewdns',
  pius_whoxy: 'whoxy',
  pius_builtwith: 'builtwith',
};

export interface PluginParam {
  name: string;
  description: string;
  defaultValue?: string;
  type: 'string' | 'int' | 'bool';
}

export const PLUGIN_PARAMS: Record<
  string,
  { mode: 'active' | 'passive'; label: string; params: PluginParam[] }
> = {
  'doh-enum': {
    mode: 'active',
    label: 'DoH Enumeration',
    params: [
      {
        name: 'doh_servers',
        description: 'Comma-separated DoH servers',
        type: 'string',
      },
      {
        name: 'doh_gateways',
        description: 'Comma-separated DoH gateways',
        type: 'string',
      },
      {
        name: 'doh_deploy_gateways',
        description: 'Deploy gateways',
        type: 'bool',
        defaultValue: 'false',
      },
    ],
  },
  'dns-brute': {
    mode: 'active',
    label: 'DNS Brute Force',
    params: [
      {
        name: 'dns_brute_concurrency',
        description: 'Concurrency level',
        type: 'int',
        defaultValue: '100',
      },
    ],
  },
  'google-dorks': {
    mode: 'passive',
    label: 'Google Dorks',
    params: [
      {
        name: 'google_dorks_max_subsidiaries',
        description: 'Max subsidiaries to dork',
        type: 'int',
        defaultValue: '10',
      },
    ],
  },
};
